// PaymentGatewayApi.tsx
import baseGoApi from "@/api/baseGoApi.ts";

export interface SwishConfig {
    payeeAlias: string | null;
    active: boolean;
}

export interface StripeConfig {
    id?: number;
    publishableKey: string | null;
    secretKey: string | null;
    active: boolean;
}

export interface AtClubConfig {
    id?: number;
    active: boolean;
}

export interface PayPalConfig {
    id?: number;
    clientId: string | null;
    clientSecret: string | null;
    active: boolean;
}

export interface RevolutConfig {
    id?: number;
    clientId: string | null;
    clientSecret: string | null;
    active: boolean;
}

// Swish API
export const getAllPaymentGateways = async (): Promise<string[]> => {
    const response = await baseGoApi.get('/paymentgateway/');
    const data = response.data || [];
    return (Array.isArray(data) ? data : [])
        .filter((g: any) => Boolean(g?.active))
        .map((g: any) => String(g?.Provider ?? g?.provider ?? g?.name ?? ''))
        .filter((s: string) => s.length > 0)
        .map((s: string) => s.toUpperCase());
};

// Fetch available payment gateways for a specific payment receiver
export const getPaymentGatewaysForReceiver = async (
    paymentReceiverId: number
): Promise<string[]> => {
    const response = await baseGoApi.get(`/paymentgateway/receiver/${paymentReceiverId}`);
    const data = response.data || [];
    return (Array.isArray(data) ? data : [])
        .filter((g: any) => Boolean(g?.active))
        .map((g: any) => String(g?.Provider ?? g?.provider ?? g?.name ?? ''))
        .filter((s: string) => s.length > 0)
        .map((s: string) => s.toUpperCase());
};

export const getSwishConfig = async (paymentReceiverId?: string): Promise<SwishConfig> => {
    try {
        const url = paymentReceiverId
            ? `/paymentgateway/provider/SWISH/${paymentReceiverId}`
            : '/paymentgateway/config/swish';
        const response = await baseGoApi.get(url);
        const data = response.data;

        return {
            payeeAlias: data.payeeAlias ?? data.config?.payeeAlias ?? null,
            active: data.active || false
        };
    } catch (error) {
        return {
            payeeAlias: null,
            active: false
        };
    }
};

export const updateSwishConfig = async (
    config: Partial<SwishConfig>,
    paymentReceiverId?: string
): Promise<SwishConfig> => {
    if (paymentReceiverId) {
        const payload = {
            active: config.active,
            provider: 'SWISH',
            paymentReceiverID: Number(paymentReceiverId),
            config: JSON.stringify({
                payeeAlias: config.payeeAlias
            })
        };
        const response = await baseGoApi.post('/paymentgateway/', payload);
        return response.data.config || response.data;
    }

    const response = await baseGoApi.post('/paymentgateway/config/swish', config);
    return response.data;
};

// Stripe API
export const getStripeConfig = async (paymentReceiverId?: string): Promise<StripeConfig> => {
    try {
        const url = paymentReceiverId 
            ? `/paymentgateway/provider/STRIPE/${paymentReceiverId}`
            : '/paymentgateway/provider/STRIPE';
        const response = paymentReceiverId 
            ? await baseGoApi.get(url)
            : await baseGoApi.get(url);
        
        const data = response.data;
        
        // Handle the new response structure where config is an object
        let config = {};
        if (data && data.config) {
            if (typeof data.config === 'string') {
                try {
                    config = JSON.parse(data.config);
                } catch (error) {
                    console.error('Failed to parse Stripe config:', error);
                }
            } else {
                config = data.config;
            }
        }
        
        return {
            id: data.id,
            publishableKey: config['publishable-key'] || config['publishableKey'] || null,
            secretKey: config['secret-key'] || config['secretKey'] || null,
            active: data.active || false
        };
    } catch (error) {
        // Log error for debugging
        console.error('Failed to load Stripe config:', error);
        // Return default config if server response is empty or error occurs
        return {
            publishableKey: null,
            secretKey: null,
            active: false
        };
    }
};

export const updateStripeConfig = async (
    config: Partial<StripeConfig>,
    paymentReceiverId?: string
): Promise<StripeConfig> => {
    const payload = {
        provider: 'STRIPE',
        active: config.active ?? false,
        ...(paymentReceiverId && { paymentReceiverID: Number(paymentReceiverId) }),
        config: JSON.stringify({
            'publishable-key': config.publishableKey,
            'secret-key': config.secretKey
        })
    };

    // Use PUT if config has an ID (existing config), otherwise use POST (new config)
    if (config.id) {
        const response = await baseGoApi.put(`/paymentgateway/${config.id}`, payload);
        return response.data;
    } else {
        const response = await baseGoApi.post('/paymentgateway/', payload);
        return response.data;
    }
};

// AtClub API
export const getAtClubConfig = async (paymentReceiverId?: string): Promise<AtClubConfig> => {
    const url = paymentReceiverId
        ? `/paymentgateway/provider/AT_CLUB/${paymentReceiverId}`
        : '/paymentgateway/provider/AT_CLUB';

    const response = await baseGoApi.get(url);
    const data = response.data || {};

    return {
        id: data.id,
        active: Boolean(data.active),
    };
};

export const updateAtClubConfig = async (
    config: any,
    _paymentReceiverId?: string
): Promise<AtClubConfig> => {
    // `config` is expected to already be shaped for the Go API
    // (see `PaymentGatewayAtClubForm`), including:
    // { id?, active, provider: 'AT_CLUB', paymentReceiverID?, config: '{}' }
    const hasId = typeof config?.id === 'number';

    if (hasId) {
        const response = await baseGoApi.put(`/paymentgateway/${config.id}`, config);
        return response.data;
    } else {
        const response = await baseGoApi.post('/paymentgateway/', config);
        return response.data;
    }
};

// PayPal API
export const getPayPalConfig = async (): Promise<PayPalConfig> => {
    try {
        const response = await baseGoApi.get('/paymentgateway/provider/PAYPAL');
        const data = response.data;
        
        // Parse the config - it can be either a JSON string or an object
        let config = {};
        if (data.config) {
            try {
                // If config is a string, parse it; if it's already an object, use it directly
                config = typeof data.config === 'string' ? JSON.parse(data.config) : data.config;
            } catch (error) {
                console.error('Failed to parse PayPal config:', error);
            }
        }
        
        return {
            id: data.id,
            clientId: config['client-id'] || null,
            clientSecret: config['client-secret'] || null,
            active: data.active || false
        };
    } catch {
        // Return default config if server response is empty or error occurs
        return {
            clientId: null,
            clientSecret: null,
            active: false
        };
    }
};

export const updatePayPalConfig = async (
    config: Partial<PayPalConfig>
): Promise<PayPalConfig> => {
    const payload = {
        provider: 'PAYPAL',
        active: config.active,
        config: JSON.stringify({
            'client-id': config.clientId,
            'client-secret': config.clientSecret,
            environment: 'production'
        })
    };

    // Use PUT if config has an ID (existing config), otherwise use POST (new config)
    if (config.id) {
        const response = await baseGoApi.put(`/paymentgateway/${config.id}`, payload);
        return response.data;
    } else {
        const response = await baseGoApi.post('/paymentgateway/', payload);
        return response.data;
    }
};

// Revolut API
export const getRevolutConfig = async (): Promise<RevolutConfig> => {
    try {
        const response = await baseGoApi.get('/paymentgateway/provider/REVOLUT');
        const data = response.data;
        
        // Parse the config - it can be either a JSON string or an object
        let config = {};
        if (data.config) {
            try {
                // If config is a string, parse it; if it's already an object, use it directly
                config = typeof data.config === 'string' ? JSON.parse(data.config) : data.config;
            } catch (error) {
                console.error('Failed to parse Revolut config:', error);
            }
        }
        
        return {
            id: data.id,
            clientId: config['client-id'] || null,
            clientSecret: config['client-secret'] || null,
            active: data.active || false
        };
    } catch {
        // Return default config if server response is empty or error occurs
        return {
            clientId: null,
            clientSecret: null,
            active: false
        };
    }
};

export const updateRevolutConfig = async (
    config: Partial<RevolutConfig>
): Promise<RevolutConfig> => {
    const payload = {
        provider: 'REVOLUT',
        active: config.active,
        config: JSON.stringify({
            'client-id': config.clientId,
            'client-secret': config.clientSecret,
            environment: 'production'
        })
    };

    // Use PUT if config has an ID (existing config), otherwise use POST (new config)
    if (config.id) {
        const response = await baseGoApi.put(`/paymentgateway/${config.id}`, payload);
        return response.data;
    } else {
        const response = await baseGoApi.post('/paymentgateway/', payload);
        return response.data;
    }
};